import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader, Popup } from 'semantic-ui-react';

import Api from 'utils/resourcesApi';
import { formatSum } from 'utils/fieldsConversion';

// THE CARD BEHIND THE (i) OF A PAYMENT (FIN-1346). A payment has no page, so the (i) in
// Related Docs opens a card: who entered it and when, who changed it last, the sum with its
// rate to USD, what of it went to the document the card is opened from, the bank charges, the
// instruction it was paid by, the other invoices it covers. The backend sends only what the
// payment has; every line here is drawn only when its field came.

const link = (doc) => <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.number}</a>;

const money = (sum, currency) => `${formatSum(sum, true)} ${currency}`;

const line = (label, children) => (
  <div className="payment-details__line">{label}: {children}</div>
);

export const PaymentDetails = ({ details, loading, error }) => {
  if (loading) return <Loader active inline size="small" />;
  if (error) return <div className="payment-details__error">{error}</div>;
  if (!details) return null;

  const {
    entered_by: enteredBy, last_change: lastChange, ref_number: refNumber, paid_at: paidAt, received_at: receivedAt,
    sum, currency, rate, sum_usd: sumUsd, applied, cgs, payment_instruction: instruction, other_invoices: others,
    description
  } = details;

  return (
    <div className="payment-details" style={{ minWidth: '260px' }}>
      {enteredBy && line('Entered by', <>{enteredBy.name}, {enteredBy.at}</>)}
      {lastChange && line('Last change', <>{lastChange.name}, {lastChange.at}</>)}
      {refNumber && line('Ref', <>{refNumber}</>)}
      {paidAt && line('Paid at', <>{paidAt}</>)}
      {receivedAt && line('Received at', <>{receivedAt}</>)}
      {sum !== undefined && line('Sum', <>{money(sum, currency)}</>)}
      {rate && line('Rate', <>1 {currency} = {rate} USD</>)}
      {sumUsd !== undefined && line('Sum in USD', <>{formatSum(sumUsd, true)}</>)}
      {applied && line('Applied to this invoice', <>
          {formatSum(applied.sum, true)} of {money(applied.invoice_sum, applied.currency)}
        </>)}
      {cgs && line('Bank CGS', <>
          {money(cgs.sum, currency)}{cgs.invoice && <>, invoice {link(cgs.invoice)}</>}
        </>)}
      {instruction && line('Payment Instruction', <>{link(instruction)}</>)}
      {others && line('Other invoices', <>
          {others.map((invoice, i) => (
            <span key={invoice.id}>{i > 0 && ', '}{link(invoice)} ({money(invoice.sum, invoice.currency)})</span>
          ))}
        </>)}
      {description && line('Description', <>{description}</>)}
    </div>
  );
};

PaymentDetails.propTypes = {
  details: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string
};

const CLOSED = { loading: false, details: null, error: null };

// The popup on the (i): opens on hover (and on a click, for a touch screen), asks the card
// once per open, for the document the list belongs to when that is an invoice.
const PaymentDetailsPopup = ({ paymentId, owner, trigger }) => {
  const [state, setState] = useState(CLOSED);
  const request = useRef(0);

  const handleOpen = useCallback(() => {
    request.current += 1;
    const token = request.current;
    const invoiceId = owner && owner.type === 'Invoice' ? owner.id : undefined;
    setState({ ...CLOSED, loading: true });
    Api.payments.details(paymentId, invoiceId)
      .then(({ data }) => request.current === token && setState({ ...CLOSED, details: (data || {}).data }))
      .catch((error) => request.current === token && setState({ ...CLOSED, error: (error && error.message) || 'Could not load the payment' }));
  }, [paymentId, owner]);

  return (
    <Popup
      trigger={trigger}
      on={['hover', 'click']}
      hoverable
      position="right center"
      mouseEnterDelay={300}
      mouseLeaveDelay={250}
      onOpen={handleOpen}
      content={<PaymentDetails details={state.details} loading={state.loading} error={state.error} />}
    />
  );
};

PaymentDetailsPopup.propTypes = {
  paymentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  owner: PropTypes.shape({ type: PropTypes.string, id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
  trigger: PropTypes.element.isRequired
};

export default PaymentDetailsPopup;
